import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = process.env.REACT_APP_API_URL || '';

function App() {
  const [isOnline, setIsOnline] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('all');
  const [docsContent, setDocsContent] = useState('');
  const [docsLoading, setDocsLoading] = useState(false);
  const [sortField, setSortField] = useState('confidence');
  const [sortDir, setSortDir] = useState('asc');
  const [playingFile, setPlayingFile] = useState(null);
  const [playerPosition, setPlayerPosition] = useState({ x: 20, y: 20 });
  const [humanClassifications, setHumanClassifications] = useState({});
  const [selectedForTraining, setSelectedForTraining] = useState({});
  const [currentPage, setCurrentPage] = useState('classify'); // 'classify', 'cleanup', or 'reports'
  const [storedJobs, setStoredJobs] = useState([]);
  const [selectedJobsForDeletion, setSelectedJobsForDeletion] = useState({});
  const [selectedJobsForReport, setSelectedJobsForReport] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [jobName, setJobName] = useState('');
  const [reportName, setReportName] = useState('');
  const [classifyMode, setClassifyMode] = useState('compare'); // 'compare' or 'blind'
  const [currentJobMode, setCurrentJobMode] = useState('compare'); // mode of currently loaded job
  const [theme, setTheme] = useState(() => localStorage.getItem('amd-theme') || 'dark');
  const [editingJobId, setEditingJobId] = useState(null);
  const [editingJobName, setEditingJobName] = useState('');
  const fileInputRef = useRef(null);
  const pollInterval = useRef(null);

  // Auto-save human reviews when they change
  useEffect(() => {
    if (jobId && Object.keys(humanClassifications).length > 0) {
      const saveReviews = async () => {
        try {
          await axios.post(`${API_URL}/api/job/${jobId}/reviews`, {
            humanReviews: humanClassifications
          });
        } catch (err) {
          console.error('Failed to save reviews:', err);
        }
      };
      const timer = setTimeout(saveReviews, 1000); // Debounce 1 second
      return () => clearTimeout(timer);
    }
  }, [humanClassifications, jobId]);

  // Load human reviews when job completes
  useEffect(() => {
    if (jobId && jobStatus?.status === 'complete') {
      const loadReviews = async () => {
        try {
          const res = await axios.get(`${API_URL}/api/job/${jobId}/reviews`);
          if (res.data.humanReviews && Object.keys(res.data.humanReviews).length > 0) {
            setHumanClassifications(res.data.humanReviews);
          }
        } catch (err) {
          console.error('Failed to load reviews:', err);
        }
      };
      loadReviews();
    }
  }, [jobId, jobStatus?.status]);

  // Check API health
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/health`);
        setIsOnline(res.data.status === 'healthy' && res.data.model_loaded);
      } catch {
        setIsOnline(false);
      }
    };
    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch documentation when docs page is opened
  useEffect(() => {
    if (currentPage === 'docs') {
      setDocsLoading(true);
      fetch('/DOCS.md?t=' + Date.now())  // Cache bust
        .then(res => res.text())
        .then(text => {
          setDocsContent(text);
          setDocsLoading(false);
        })
        .catch(err => {
          console.error('Failed to load docs:', err);
          setDocsContent('# Error\n\nFailed to load documentation.');
          setDocsLoading(false);
        });
    }
  }, [currentPage]);

  // Poll job status
  useEffect(() => {
    if (jobId && jobStatus?.status === 'processing') {
      pollInterval.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API_URL}/api/job/${jobId}`);
          setJobStatus(res.data);
          if (res.data.status === 'complete') {
            setResults(res.data.results || []);
            setSummary(res.data.summary || null);
            clearInterval(pollInterval.current);
          } else if (res.data.status === 'error') {
            clearInterval(pollInterval.current);
          }
        } catch (err) {
          console.error('Poll error:', err);
        }
      }, 1000);
    }
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [jobId, jobStatus?.status]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => 
      /\.(wav|mp3|m4a|au|zip)$/i.test(f.name)
    );
    setSelectedFiles(files);
  }, []);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    // Show uploading state immediately
    setJobStatus({ status: 'uploading', progress: 0 });

    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('files', file);
    });
    formData.append('job_name', jobName);
    formData.append('mode', classifyMode);

    try {
      const res = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setJobStatus({ status: 'uploading', progress: percentCompleted });
        }
      });
      setJobId(res.data.job_id);
      setJobStatus({ status: 'processing', total: res.data.total_files, processed: 0, progress: 2 });
      setCurrentJobMode(classifyMode);
      setResults([]);
      setSummary(null);
      setSelectedFiles([]);
      setHumanClassifications({});
      setSelectedForTraining({});
      setJobName('');
    } catch (err) {
      console.error('Upload error:', err);
      setJobStatus(null);
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDownload = async (folder) => {
    if (!jobId) return;
    window.open(`${API_URL}/api/job/${jobId}/download/${folder}`, '_blank');
  };

  const handlePlay = (filename) => {
    if (!jobId) return;
    
    // If same file, replay it
    if (playingFile?.name === filename) {
      const audioEl = document.querySelector('.audio-player audio');
      if (audioEl) {
        audioEl.currentTime = 0;
        audioEl.play();
      }
      return;
    }
    
    setPlayingFile({
      name: filename,
      url: `${API_URL}/api/job/${jobId}/audio/${filename}`
    });
  };

  const handleHumanClassification = (filename, value) => {
    setHumanClassifications(prev => ({
      ...prev,
      [filename]: value
    }));
  };

  const handleTrainingSelect = (filename) => {
    setSelectedForTraining(prev => ({
      ...prev,
      [filename]: !prev[filename]
    }));
  };

  const handleSelectAllForTraining = () => {
    const disagreements = results.filter(r => r.match === 'DISAGREE');
    const allSelected = disagreements.every(r => selectedForTraining[r.filename]);
    
    const newSelections = {};
    disagreements.forEach(r => {
      newSelections[r.filename] = !allSelected;
    });
    setSelectedForTraining(prev => ({
      ...prev,
      ...newSelections
    }));
  };

  const handleDownloadCSV = () => {
    const disagreements = results.filter(r => r.match === 'DISAGREE');
    
    const csvRows = [
      ['Filename', 'Human Classification', 'Model Prediction', 'Confidence', 'System Classification', 'Model Correct', 'System Correct'].join(',')
    ];
    
    disagreements.forEach(r => {
      const human = humanClassifications[r.filename] || '';
      const modelCorrect = human ? (human === r.model_prediction ? 'Yes' : 'No') : '';
      const systemCorrect = human ? (human === r.system_normalized ? 'Yes' : 'No') : '';
      
      csvRows.push([
        r.filename,
        human,
        r.model_prediction,
        r.confidence,
        r.system_classification,
        modelCorrect,
        systemCorrect
      ].join(','));
    });
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'disagreements_review.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadSelectedForTraining = async () => {
    const selected = Object.entries(selectedForTraining)
      .filter(([_, isSelected]) => isSelected)
      .map(([filename, _]) => filename);
    
    if (selected.length === 0) {
      alert('No files selected');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/job/${jobId}/download-selected`,
        { filenames: selected },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'selected_files.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download selected files');
    }
  };

  const handleDownloadModelCorrections = async () => {
    // Find all files where human review disagrees with model prediction
    const amdCorrections = []; // Human said AMD, model said something else
    const pvdCorrections = []; // Human said PVD, model said something else
    
    results.forEach(r => {
      const humanReview = humanClassifications[r.filename];
      if (!humanReview || humanReview === 'unknown') return;
      
      const modelPred = r.model_prediction;
      
      if (humanReview === 'voicemail' && modelPred !== 'voicemail') {
        amdCorrections.push(r.filename);
      } else if (humanReview === 'person' && modelPred !== 'person') {
        pvdCorrections.push(r.filename);
      }
    });
    
    if (amdCorrections.length === 0 && pvdCorrections.length === 0) {
      alert('No model corrections found. Review files and mark where the model was wrong.');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/job/${jobId}/training-data`,
        { amdCorrections, pvdCorrections },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'model_corrections.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Training data download failed:', err);
      alert('Failed to download training data');
    }
  };

  const getModelCorrectionCounts = () => {
    let amdCount = 0;
    let pvdCount = 0;
    
    results.forEach(r => {
      const humanReview = humanClassifications[r.filename];
      if (!humanReview || humanReview === 'unknown') return;
      
      const modelPred = r.model_prediction;
      
      if (humanReview === 'voicemail' && modelPred !== 'voicemail') {
        amdCount++;
      } else if (humanReview === 'person' && modelPred !== 'person') {
        pvdCount++;
      }
    });
    
    return { amdCount, pvdCount, total: amdCount + pvdCount };
  };

  const handleGenerateReport = async () => {
    if (!jobId) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/job/${jobId}/report`,
        { humanClassifications },
        { responseType: 'blob' }
      );
      
      // Open HTML in new window for printing to PDF
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Report generation failed:', err);
      alert('Failed to generate report');
    }
  };

  const getFilteredResults = () => {
    let filtered = [...results];
    
    if (filter === 'amd') {
      filtered = filtered.filter(r => r.model_prediction === 'voicemail');
    } else if (filter === 'pvd') {
      filtered = filtered.filter(r => r.model_prediction === 'person');
    } else if (filter === 'no_audio') {
      filtered = filtered.filter(r => r.model_prediction === 'no_audio');
    } else if (filter === 'disagree') {
      filtered = filtered.filter(r => r.match === 'DISAGREE');
    } else if (filter === 'agree') {
      filtered = filtered.filter(r => r.match === 'AGREE');
    } else if (filter === 'unknown') {
      // Only show files where system actually said "Unknown" (didn't decide)
      // Exclude no_audio files where match is N/A just because we skip comparison
      filtered = filtered.filter(r => r.system_classification === 'Unknown');
    } else if (filter === 'sys_pvd') {
      // System classified as person (sent to agent)
      filtered = filtered.filter(r => r.system_normalized === 'person');
    } else if (filter === 'sys_amd') {
      // System classified as voicemail
      filtered = filtered.filter(r => r.system_normalized === 'voicemail');
    }

    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortDir === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return filtered;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const getConfidenceClass = (confidence) => {
    if (confidence >= 80) return 'high';
    if (confidence >= 60) return 'medium';
    return 'low';
  };

  // Always show human review column so users can mark corrections for training
  const showHumanReviewColumns = true;
  const selectedCount = Object.values(selectedForTraining).filter(Boolean).length;

  // Load stored jobs on mount and when switching to cleanup or reports page
  useEffect(() => {
    loadStoredJobs();
  }, []);

  useEffect(() => {
    if (currentPage === 'cleanup' || currentPage === 'reports') {
      loadStoredJobs();
    }
  }, [currentPage]);

  const loadStoredJobs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/jobs`);
      setStoredJobs(res.data.jobs || []);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    }
  };

  const handleJobSelectionForDeletion = (folderName) => {
    setSelectedJobsForDeletion(prev => ({
      ...prev,
      [folderName]: !prev[folderName]
    }));
  };

  const handleSelectAllJobsForDeletion = () => {
    const allSelected = storedJobs.every(j => selectedJobsForDeletion[j.folder_name]);
    const newSelections = {};
    storedJobs.forEach(j => {
      newSelections[j.folder_name] = !allSelected;
    });
    setSelectedJobsForDeletion(newSelections);
  };

  const handleDeleteSelectedJobs = async () => {
    const foldersToDelete = Object.entries(selectedJobsForDeletion)
      .filter(([_, selected]) => selected)
      .map(([folder, _]) => folder);

    if (foldersToDelete.length === 0) {
      alert('No jobs selected for deletion');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${foldersToDelete.length} job(s)? This cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    try {
      const res = await axios.post(`${API_URL}/api/jobs/delete`, {
        folders: foldersToDelete
      });
      
      alert(`Deleted ${res.data.deleted_count} job(s)`);
      setSelectedJobsForDeletion({});
      loadStoredJobs();
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete jobs: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsDeleting(false);
    }
  };

  const getTotalSelectedSize = () => {
    const selectedJobs = storedJobs.filter(j => selectedJobsForDeletion[j.folder_name]);
    const totalBytes = selectedJobs.reduce((sum, j) => sum + j.size_bytes, 0);
    
    if (totalBytes < 1024) return `${totalBytes} B`;
    if (totalBytes < 1024 * 1024) return `${(totalBytes / 1024).toFixed(1)} KB`;
    if (totalBytes < 1024 * 1024 * 1024) return `${(totalBytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(totalBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const selectedJobCount = Object.values(selectedJobsForDeletion).filter(Boolean).length;

  const handleJobSelectionForReport = (folderName) => {
    setSelectedJobsForReport(prev => ({
      ...prev,
      [folderName]: !prev[folderName]
    }));
  };

  const handleSelectAllJobsForReport = () => {
    const allSelected = storedJobs.every(j => selectedJobsForReport[j.folder_name]);
    const newSelections = {};
    storedJobs.forEach(j => {
      newSelections[j.folder_name] = !allSelected;
    });
    setSelectedJobsForReport(newSelections);
  };

  const handleGenerateCombinedReport = async () => {
    const selectedFolders = Object.entries(selectedJobsForReport)
      .filter(([_, selected]) => selected)
      .map(([folder, _]) => folder);

    if (selectedFolders.length === 0) {
      alert('No jobs selected for report');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/reports/combined`,
        { 
          folders: selectedFolders,
          report_name: reportName || 'Combined Report'
        },
        { responseType: 'text' }
      );
      
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      
      // Clean up blob URL after window opens
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (err) {
      console.error('Report generation failed:', err);
      alert('Failed to generate report: ' + (err.response?.data?.error || err.message));
    }
  };

  const selectedReportJobCount = Object.values(selectedJobsForReport).filter(Boolean).length;

  const handleLoadJob = async (job) => {
    try {
      const res = await axios.get(`${API_URL}/api/job/${job.job_id}`);
      if (res.data.status === 'complete') {
        setJobId(job.job_id);
        setJobName(job.job_name || job.folder_name || '');
        setJobStatus({ status: 'complete', total: res.data.total, processed: res.data.processed, progress: 100 });
        setResults(res.data.results || []);
        setSummary(res.data.summary || null);
        setCurrentJobMode(res.data.mode || job.mode || 'compare');
        setFilter('all');
        setHumanClassifications({});
        setSelectedForTraining({});
        // Load human reviews
        try {
          const reviewsRes = await axios.get(`${API_URL}/api/job/${job.job_id}/reviews`);
          if (reviewsRes.data.humanReviews) {
            setHumanClassifications(reviewsRes.data.humanReviews);
          }
        } catch (err) {
          console.error('Failed to load reviews:', err);
        }
        setCurrentPage('classify');
      } else {
        alert('Job is not complete');
      }
    } catch (err) {
      console.error('Failed to load job:', err);
      alert('Failed to load job: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditJobName = (job) => {
    setEditingJobId(job.job_id);
    setEditingJobName(job.job_name || '');
  };

  const handleSaveJobName = async (job) => {
    try {
      await axios.post(`${API_URL}/api/job/${job.job_id}/rename`, {
        job_name: editingJobName
      });
      // Update local state
      setStoredJobs(prev => prev.map(j => 
        j.job_id === job.job_id ? { ...j, job_name: editingJobName } : j
      ));
      // Also update currently displayed jobName if this is the loaded job
      if (job.job_id === jobId) {
        setJobName(editingJobName);
      }
      setEditingJobId(null);
      setEditingJobName('');
    } catch (err) {
      console.error('Failed to rename job:', err);
      alert('Failed to rename job');
    }
  };

  const handleCancelEdit = () => {
    setEditingJobId(null);
    setEditingJobName('');
  };

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('amd-theme', theme);
  }, [theme]);

  return (
    <div className="app">
      <header>
        <h1>📞 TrueCalls</h1>
        <nav className="nav-tabs">
          <button 
            className={`nav-tab ${currentPage === 'classify' ? 'active' : ''}`}
            onClick={() => setCurrentPage('classify')}
          >
            Classify
          </button>
          <button 
            className={`nav-tab ${currentPage === 'jobs' ? 'active' : ''}`}
            onClick={() => setCurrentPage('jobs')}
          >
            Jobs
          </button>
          <button 
            className={`nav-tab ${currentPage === 'reports' ? 'active' : ''}`}
            onClick={() => setCurrentPage('reports')}
          >
            Reports
          </button>
          <button 
            className={`nav-tab ${currentPage === 'cleanup' ? 'active' : ''}`}
            onClick={() => setCurrentPage('cleanup')}
          >
            Cleanup
          </button>
          <button 
            className={`nav-tab ${currentPage === 'docs' ? 'active' : ''}`}
            onClick={() => setCurrentPage('docs')}
          >
            Docs
          </button>
        </nav>
        <div className="header-controls">
          <select 
            className="theme-select"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
          >
            <option value="dark">🌙 Dark</option>
            <option value="light">☀️ Light</option>
            <option value="midnight">🌌 Midnight</option>
            <option value="forest">🌲 Forest</option>
            <option value="forest-light">🌿 Forest Light</option>
            <option value="ocean">🌊 Ocean</option>
            <option value="sunset">🌅 Sunset</option>
            <option value="sunset-light">🍑 Sunset Light</option>
          </select>
          <div className="status-badge">
            <span className={`status-dot ${isOnline ? '' : 'offline'}`}></span>
            {isOnline ? 'Model Ready' : 'Offline'}
          </div>
        </div>
      </header>

      {currentPage === 'jobs' ? (
        /* Jobs Page */
        <div className="cleanup-section">
          <div className="cleanup-header">
            <h2>📁 Saved Jobs</h2>
            <p>Load a previous job to review results</p>
          </div>

          {storedJobs.length === 0 ? (
            <div className="no-jobs">
              <p>No saved jobs found</p>
            </div>
          ) : (
            <table className="cleanup-table">
              <thead>
                <tr>
                  <th>Job Name</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>AMD</th>
                  <th>PVD</th>
                  <th>No Audio</th>
                  <th>Agreement</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {storedJobs.map((job) => (
                  <tr key={job.folder_name}>
                    <td>
                      {editingJobId === job.job_id ? (
                        <div className="edit-name-row">
                          <input
                            type="text"
                            className="edit-name-input"
                            value={editingJobName}
                            onChange={(e) => setEditingJobName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveJobName(job);
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                            autoFocus
                          />
                          <button className="save-btn" onClick={() => handleSaveJobName(job)}>✓</button>
                          <button className="cancel-btn" onClick={handleCancelEdit}>✕</button>
                        </div>
                      ) : (
                        <div className="job-name-cell">
                          <span>{job.job_name || job.folder_name}</span>
                          <button className="edit-btn" onClick={() => handleEditJobName(job)} title="Edit name">✏️</button>
                        </div>
                      )}
                    </td>
                    <td>{new Date(job.created).toLocaleString()}</td>
                    <td>{job.summary?.total || 0}</td>
                    <td>{job.summary?.amd || 0}</td>
                    <td>{job.summary?.pvd || 0}</td>
                    <td>{job.summary?.no_audio || 0}</td>
                    <td>{job.summary?.accuracy ? `${job.summary.accuracy}%` : '-'}</td>
                    <td>
                      <button 
                        className="load-btn"
                        onClick={() => handleLoadJob(job)}
                      >
                        Load
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : currentPage === 'cleanup' ? (
        /* Cleanup Page */
        <div className="cleanup-section">
          <div className="cleanup-header">
            <h2>🗑️ Cleanup Jobs</h2>
            <p>Remove old job folders to free up disk space</p>
          </div>

          {storedJobs.length === 0 ? (
            <div className="no-jobs">
              <p>No stored jobs found</p>
            </div>
          ) : (
            <>
              <div className="cleanup-actions">
                <button 
                  className="delete-btn"
                  onClick={handleDeleteSelectedJobs}
                  disabled={selectedJobCount === 0 || isDeleting}
                >
                  {isDeleting ? 'Deleting...' : `Delete Selected (${selectedJobCount})`}
                </button>
                {selectedJobCount > 0 && (
                  <span className="selected-size">
                    {getTotalSelectedSize()} will be freed
                  </span>
                )}
              </div>

              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>
                        <input 
                          type="checkbox"
                          onChange={handleSelectAllJobsForDeletion}
                          checked={storedJobs.length > 0 && storedJobs.every(j => selectedJobsForDeletion[j.folder_name])}
                        />
                      </th>
                      <th>Job Name</th>
                      <th>Date</th>
                      <th>Files</th>
                      <th>Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storedJobs.map((job, i) => (
                      <tr key={i}>
                        <td>
                          <input 
                            type="checkbox"
                            checked={selectedJobsForDeletion[job.folder_name] || false}
                            onChange={() => handleJobSelectionForDeletion(job.folder_name)}
                          />
                        </td>
                        <td>{job.job_name || job.folder_name}</td>
                        <td>{job.display_date}</td>
                        <td>{job.file_count}</td>
                        <td>{job.size_display}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ) : currentPage === 'reports' ? (
        /* Reports Page */
        <div className="reports-section">
          <div className="reports-header">
            <h2>📊 Combined Reports</h2>
            <p>Select multiple jobs to generate a combined report</p>
          </div>

          {storedJobs.length === 0 ? (
            <div className="no-jobs">
              <p>No completed jobs found</p>
            </div>
          ) : (
            <>
              <div className="reports-actions">
                <input
                  type="text"
                  className="report-name-input"
                  placeholder="Report name (e.g., Customer ABC - Q1 Analysis)"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                />
                <button 
                  className="generate-report-btn"
                  onClick={handleGenerateCombinedReport}
                  disabled={selectedReportJobCount === 0}
                >
                  📊 Generate Combined Report ({selectedReportJobCount} jobs)
                </button>
              </div>

              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>
                        <input 
                          type="checkbox"
                          onChange={handleSelectAllJobsForReport}
                          checked={storedJobs.length > 0 && storedJobs.every(j => selectedJobsForReport[j.folder_name])}
                        />
                      </th>
                      <th>Job Name</th>
                      <th>Date</th>
                      <th>Total</th>
                      <th>AMD</th>
                      <th>PVD</th>
                      <th>Agreement</th>
                      <th>Reviews</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storedJobs.map((job, i) => (
                      <tr key={i}>
                        <td>
                          <input 
                            type="checkbox"
                            checked={selectedJobsForReport[job.folder_name] || false}
                            onChange={() => handleJobSelectionForReport(job.folder_name)}
                          />
                        </td>
                        <td>{job.job_name || job.folder_name}</td>
                        <td>{job.display_date}</td>
                        <td>{job.summary?.total || '-'}</td>
                        <td>{job.summary?.amd || '-'}</td>
                        <td>{job.summary?.pvd || '-'}</td>
                        <td>{job.summary?.accuracy ? `${job.summary.accuracy}%` : '-'}</td>
                        <td>{job.human_reviews_count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ) : currentPage === 'docs' ? (
        /* Documentation Page */
        <div className="docs-section">
          <div className="docs-container">
            <nav className="docs-toc">
              <h3>📖 Contents</h3>
              <ul>
                <li><a href="#overview" onClick={(e) => { e.preventDefault(); document.getElementById('overview')?.scrollIntoView({ behavior: 'smooth' }); }}>Overview</a></li>
                <li><a href="#getting-started" onClick={(e) => { e.preventDefault(); document.getElementById('getting-started')?.scrollIntoView({ behavior: 'smooth' }); }}>Getting Started</a></li>
                <li><a href="#classification-modes" onClick={(e) => { e.preventDefault(); document.getElementById('classification-modes')?.scrollIntoView({ behavior: 'smooth' }); }}>Classification Modes</a></li>
                <li><a href="#understanding-results" onClick={(e) => { e.preventDefault(); document.getElementById('understanding-results')?.scrollIntoView({ behavior: 'smooth' }); }}>Understanding Results</a></li>
                <li><a href="#the-model" onClick={(e) => { e.preventDefault(); document.getElementById('the-model')?.scrollIntoView({ behavior: 'smooth' }); }}>The Model</a></li>
                <li><a href="#reports--accuracy-metrics" onClick={(e) => { e.preventDefault(); document.getElementById('reports--accuracy-metrics')?.scrollIntoView({ behavior: 'smooth' }); }}>Reports & Accuracy Metrics</a></li>
                <li><a href="#example-analysis-walkthrough" onClick={(e) => { e.preventDefault(); document.getElementById('example-analysis-walkthrough')?.scrollIntoView({ behavior: 'smooth' }); }}>Example Analysis</a></li>
                <li><a href="#human-review--training" onClick={(e) => { e.preventDefault(); document.getElementById('human-review--training')?.scrollIntoView({ behavior: 'smooth' }); }}>Human Review & Training</a></li>
                <li><a href="#file-naming-convention" onClick={(e) => { e.preventDefault(); document.getElementById('file-naming-convention')?.scrollIntoView({ behavior: 'smooth' }); }}>File Naming Convention</a></li>
              </ul>
            </nav>
            
            <div className="docs-content">
              {docsLoading ? (
                <div className="docs-loading">Loading documentation...</div>
              ) : (
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h2: ({node, children, ...props}) => {
                      const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                      return <h2 id={id} {...props}>{children}</h2>;
                    },
                    a: ({node, href, children, ...props}) => {
                      if (href && href.startsWith('#')) {
                        return (
                          <a 
                            href={href} 
                            onClick={(e) => { 
                              e.preventDefault(); 
                              document.getElementById(href.slice(1))?.scrollIntoView({ behavior: 'smooth' }); 
                            }}
                            {...props}
                          >
                            {children}
                          </a>
                        );
                      }
                      return <a href={href} {...props}>{children}</a>;
                    },
                    table: ({node, ...props}) => <table className="docs-table" {...props} />,
                    blockquote: ({node, ...props}) => <blockquote className="docs-note" {...props} />
                  }}
                >
                  {docsContent}
                </ReactMarkdown>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Classify Page */
        <>

      {/* Welcome Section - hide when job is loaded */}
      {results.length === 0 && (
        <div className="welcome-section">
          <h2>Welcome to TrueCalls</h2>
          <p>
            TrueCalls evaluates your AMD (Answering Machine Detection) system's accuracy. 
            Upload call recordings and our machine learning model will independently classify 
            them as <strong>Voicemail</strong>, <strong>Live Person</strong>, or <strong>No Audio</strong> — then 
            compare against your system's classifications to identify discrepancies and measure accuracy.
          </p>
        </div>
      )}

      {/* Upload Section - hide when job is loaded */}
      {results.length === 0 && (
        <div 
          className={`upload-section ${isDragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <h2>Upload Audio Files</h2>
          <p>Drag and drop audio files here, or click to select</p>
          <input
            type="file"
            ref={fileInputRef}
            className="file-input"
            multiple
            onChange={handleFileSelect}
          />
          <div className="job-name-input">
            <input
              type="text"
              placeholder="Job name (e.g., Customer ABC - Batch 1)"
              value={jobName}
              onChange={(e) => setJobName(e.target.value)}
            />
          </div>
          <div className="mode-toggle">
            <button 
              className={`mode-btn ${classifyMode === 'compare' ? 'active' : ''}`}
              onClick={() => setClassifyMode('compare')}
            >
              Classify & Compare
            </button>
            <button 
              className={`mode-btn ${classifyMode === 'blind' ? 'active' : ''}`}
              onClick={() => setClassifyMode('blind')}
            >
              Blind Classify
            </button>
          </div>
          <button 
            className="upload-btn"
            onClick={() => selectedFiles.length > 0 ? handleUpload() : fileInputRef.current?.click()}
            disabled={!isOnline || (jobStatus?.status === 'processing')}
          >
          {selectedFiles.length > 0 ? `${classifyMode === 'compare' ? 'Classify & Compare' : 'Blind Classify'} ${selectedFiles.length} Files` : 'Select Files'}
        </button>
        
        {selectedFiles.length > 0 && (
          <div className="selected-files">
            <p>{selectedFiles.length} files selected</p>
            <div className="file-list">
              {selectedFiles.slice(0, 5).map((f, i) => (
                <div key={i}>{f.name}</div>
              ))}
              {selectedFiles.length > 5 && <div>...and {selectedFiles.length - 5} more</div>}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Progress Section */}
      {jobStatus?.status === 'uploading' && (
        <div className="progress-section">
          <div className="progress-header">
            <h3>Uploading...</h3>
            <span>{jobStatus.progress}%</span>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar uploading" style={{ width: `${jobStatus.progress}%` }}></div>
          </div>
          <div className="progress-info">
            <span>Uploading file to server</span>
          </div>
        </div>
      )}

      {/* Processing Section */}
      {jobStatus?.status === 'processing' && (
        <div className="progress-section">
          <div className="progress-header">
            <h3>Processing...</h3>
            <span>{jobStatus.progress}%</span>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${jobStatus.progress}%` }}></div>
          </div>
          <div className="progress-info">
            <span>{jobStatus.processed} of {jobStatus.total} files</span>
            <span>{jobStatus.current_file}</span>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="summary-section">
          <div 
            className={`summary-card clickable ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            <h4>Total Files</h4>
            <div className="value">{summary.total}</div>
            <div style={{color: '#888', fontSize: '0.85rem', marginTop: '5px'}}>
              in {summary.elapsed_display}
            </div>
          </div>
          <div 
            className={`summary-card amd clickable ${filter === 'amd' ? 'active' : ''}`}
            onClick={() => setFilter('amd')}
          >
            <h4>AMD (Voicemail)</h4>
            <div className="value">{summary.amd}</div>
          </div>
          <div 
            className={`summary-card pvd clickable ${filter === 'pvd' ? 'active' : ''}`}
            onClick={() => setFilter('pvd')}
          >
            <h4>PVD (Person)</h4>
            <div className="value">{summary.pvd}</div>
          </div>
          <div 
            className={`summary-card no-audio clickable ${filter === 'no_audio' ? 'active' : ''}`}
            onClick={() => setFilter('no_audio')}
          >
            <h4>No Audio</h4>
            <div className="value">{summary.no_audio}</div>
          </div>
          {summary.comparable > 0 && (
            <>
              <div 
                className={`summary-card agree clickable ${filter === 'agree' ? 'active' : ''}`}
                onClick={() => setFilter('agree')}
              >
                <h4>Agreement</h4>
                <div className="value">{summary.accuracy}%</div>
              </div>
              <div 
                className={`summary-card disagree clickable ${filter === 'disagree' ? 'active' : ''}`}
                onClick={() => setFilter('disagree')}
              >
                <h4>Disagreements</h4>
                <div className="value">{summary.disagrees}</div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Results Table */}
      {results.length > 0 && (
        <div className="results-section">
          <div className="results-header">
            <h3>Results{jobName ? `: ${jobName}` : ''}</h3>
            <button 
              className="new-job-btn"
              onClick={() => {
                setResults([]);
                setSummary(null);
                setJobId(null);
                setJobStatus(null);
                setJobName('');
                setHumanClassifications({});
                setFilter('all');
              }}
            >
              ← New Job
            </button>
          </div>
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({results.length})
            </button>
            <button 
              className={`filter-btn ${filter === 'amd' ? 'active' : ''}`}
              onClick={() => setFilter('amd')}
            >
              AMD ({results.filter(r => r.model_prediction === 'voicemail').length})
            </button>
            <button 
              className={`filter-btn ${filter === 'pvd' ? 'active' : ''}`}
              onClick={() => setFilter('pvd')}
            >
              PVD ({results.filter(r => r.model_prediction === 'person').length})
            </button>
            <button 
              className={`filter-btn ${filter === 'no_audio' ? 'active' : ''}`}
              onClick={() => setFilter('no_audio')}
            >
              No Audio ({results.filter(r => r.model_prediction === 'no_audio').length})
            </button>
            {currentJobMode === 'compare' && (
              <>
                <button 
                  className={`filter-btn ${filter === 'agree' ? 'active' : ''}`}
                  onClick={() => setFilter('agree')}
                >
                  Agree ({results.filter(r => r.match === 'AGREE').length})
                </button>
                <button 
                  className={`filter-btn ${filter === 'disagree' ? 'active' : ''}`}
                  onClick={() => setFilter('disagree')}
                >
                  Disagree ({results.filter(r => r.match === 'DISAGREE').length})
                </button>
                <button 
                  className={`filter-btn ${filter === 'unknown' ? 'active' : ''}`}
                  onClick={() => setFilter('unknown')}
                >
                  Sys Unknown ({results.filter(r => r.system_classification === 'Unknown').length})
                </button>
                <button 
                  className={`filter-btn ${filter === 'sys_pvd' ? 'active' : ''}`}
                  onClick={() => setFilter('sys_pvd')}
                >
                  Sys PVD ({results.filter(r => r.system_normalized === 'person').length})
                </button>
                <button 
                  className={`filter-btn ${filter === 'sys_amd' ? 'active' : ''}`}
                  onClick={() => setFilter('sys_amd')}
                >
                  Sys AMD ({results.filter(r => r.system_normalized === 'voicemail').length})
                </button>
              </>
            )}
          </div>
          <div className="download-buttons">
            <button className="download-btn" onClick={() => handleDownload('AMD')}>⬇️ AMD</button>
            <button className="download-btn" onClick={() => handleDownload('PVD')}>⬇️ PVD</button>
            <button className="download-btn" onClick={() => handleDownload('NO_AUDIO')}>⬇️ No Audio</button>
            <button className="download-btn" onClick={() => handleDownload('disagreements')}>⬇️ Disagreements</button>
            <button className="download-btn report-btn" onClick={handleGenerateReport}>📊 Generate Report</button>
            <button className="download-btn" onClick={handleDownloadCSV}>📄 Download CSV</button>
            <button className="download-btn" onClick={handleDownloadSelectedForTraining}>
              🎓 Download Selected ({selectedCount})
            </button>
            <button 
              className="download-btn training-btn" 
              onClick={handleDownloadModelCorrections}
              title="Download files where human review disagrees with model, organized into AMD and PVD training folders"
            >
              🧠 Model Corrections ({getModelCorrectionCounts().total})
            </button>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>
                    <input 
                      type="checkbox" 
                      onChange={handleSelectAllForTraining}
                      checked={getFilteredResults().length > 0 && 
                               getFilteredResults().every(r => selectedForTraining[r.filename])}
                    />
                  </th>
                  <th onClick={() => handleSort('filename')}>Filename</th>
                  <th onClick={() => handleSort('model_prediction')}>Model</th>
                  <th onClick={() => handleSort('confidence')}>Confidence</th>
                  {currentJobMode === 'compare' && <th onClick={() => handleSort('system_classification')}>System</th>}
                  {currentJobMode === 'compare' && <th onClick={() => handleSort('match')}>Match</th>}
                  {showHumanReviewColumns && <th>Human</th>}
                  <th>Play</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredResults().map((result, i) => (
                  <tr 
                    key={i} 
                    className={playingFile?.name === (result.new_filename || result.filename) ? 'playing' : ''}
                  >
                    <td>
                      <input 
                        type="checkbox"
                        checked={selectedForTraining[result.filename] || false}
                        onChange={() => handleTrainingSelect(result.filename)}
                      />
                    </td>
                    <td title={result.filename}>
                      {result.filename.length > 40 
                        ? result.filename.substring(0, 40) + '...' 
                        : result.filename}
                    </td>
                    <td>
                      <span className={`prediction-badge ${result.model_prediction}`}>
                        {result.model_prediction}
                      </span>
                    </td>
                    <td>
                      <span className="confidence-bar">
                        <span 
                          className={`confidence-fill ${getConfidenceClass(result.confidence)}`}
                          style={{ width: `${result.confidence}%` }}
                        ></span>
                      </span>
                      {result.confidence}%
                    </td>
                    {currentJobMode === 'compare' && <td>{result.system_classification || '-'}</td>}
                    {currentJobMode === 'compare' && (
                      <td>
                        <span className={`match-badge ${result.match?.toLowerCase()}`}>
                          {result.match}
                        </span>
                      </td>
                    )}
                    {showHumanReviewColumns && (
                      <td>
                        <select 
                          className="human-select"
                          value={humanClassifications[result.filename] || ''}
                          onChange={(e) => handleHumanClassification(result.filename, e.target.value)}
                        >
                          <option value="">--</option>
                          <option value="voicemail">AMD</option>
                          <option value="person">PVD</option>
                          <option value="unknown">Unknown</option>
                        </select>
                      </td>
                    )}
                    <td>
                      <button 
                        className="play-btn"
                        onClick={() => handlePlay(result.new_filename || result.filename)}
                      >
                        ▶
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Audio Player */}
      {playingFile && (
        <div 
          className={`audio-player ${playingFile.loading ? 'loading' : ''}`}
          draggable="false"
          style={{
            position: 'fixed',
            bottom: playerPosition.y,
            right: playerPosition.x,
            cursor: 'move'
          }}
          onMouseDown={(e) => {
            if (e.target.tagName === 'AUDIO' || e.target.tagName === 'BUTTON') return;
            const startX = e.clientX;
            const startY = e.clientY;
            const startRight = playerPosition.x;
            const startBottom = playerPosition.y;
            
            const onMouseMove = (moveEvent) => {
              const deltaX = startX - moveEvent.clientX;
              const deltaY = startY - moveEvent.clientY;
              setPlayerPosition({
                x: Math.max(0, startRight + deltaX),
                y: Math.max(0, startBottom + deltaY)
              });
            };
            
            const onMouseUp = () => {
              document.removeEventListener('mousemove', onMouseMove);
              document.removeEventListener('mouseup', onMouseUp);
            };
            
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
          }}
        >
          <div className="player-header">
            <div className="now-playing">
              <div className="filename">{playingFile.name}</div>
              {playingFile.loading && <div className="loading-text">Loading...</div>}
            </div>
            <button className="close-player" onClick={() => setPlayingFile(null)}>×</button>
          </div>
          <audio 
            controls 
            autoPlay 
            src={playingFile.url}
            onLoadStart={() => setPlayingFile(prev => ({...prev, loading: true}))}
            onCanPlay={() => setPlayingFile(prev => ({...prev, loading: false}))}
            onError={() => setPlayingFile(prev => ({...prev, loading: false, error: true}))}
          >
            Your browser does not support audio playback.
          </audio>
          {playingFile.error && <div className="error-text">Failed to load audio</div>}
        </div>
      )}
      </>
      )}
    </div>
  );
}

export default App;
